/**
 * Сборка занятия режимов «Цифры», «Пунктуация» и «Специальные символы».
 *
 * Материал у режимов разный, поэтому разные и правила упражнения:
 *
 * - цифры набираются группами по три: число — это движение, а отдельная
 *   цифра посреди букв движением не становится;
 * - знаки препинания тренируются внутри естественных предложений: запятая в
 *   отрыве от текста не тренирует решение, куда её ставить (ТЗ §8.3);
 * - специальный символ чередуется с базовым символом той же клавиши: режим
 *   учит не символу, а Shift — нажать и отпустить вовремя.
 *
 * Сборка детерминирована по seed: то же занятие воспроизводится в тестах
 * посимвольно (решение AD-012).
 */

import type { MasteryUnit } from '@type2x/shared-contracts';
import { toMasteryUnitId } from '@type2x/shared-contracts';
import { getLayout, strokeForCharacter } from '@type2x/keyboard-layouts';
import { getSentences } from '@type2x/corpora';
import {
  buildChunk,
  chunkAlphabet,
  createRng,
  learningChunkLimit,
  type Rng,
} from '@type2x/exercise-generator';
import { reviewShare } from '@type2x/learning-engine';

import type {
  SymbolGroup,
  SymbolMode,
  SymbolsExercise,
  SymbolsPlan,
  SymbolsSnapshot,
} from '../contracts/symbols.js';
import { DIGIT_CHUNK_LENGTH, HINT_LEVEL, ITEMS_PER_EXERCISE } from '../contracts/symbols.js';
import {
  completedGroups,
  currentGroup,
  shiftHints,
  symbolGroups,
  symbolUnit,
  unlockedCharacters,
} from './groups.js';

/**
 * Сколько символов нужно, чтобы сочетание было движением.
 *
 * Группа из одной цифры дала бы «777» — удержание клавиши, а не движение.
 */
const MIN_ALPHABET = 2;

/** Знаки препинания текста: по ним проверяется, что предложение посильно. */
function marksOf(text: string): string[] {
  return [...text].filter((character) => /^\p{P}$/u.test(character));
}

/**
 * Предложения занятия.
 *
 * Берутся только те, все знаки которых уже открыты: предложение с ещё
 * незнакомым знаком потребовало бы клавиши, которую пользователю не
 * показывали. Среди посильных предпочтение — предложениям с знаками текущей
 * группы: занятие существует ради них.
 */
function sentenceItems(
  snapshot: SymbolsSnapshot,
  rng: Rng,
  group: SymbolGroup | null,
  unlocked: readonly string[],
  count: number,
): string[] {
  const open = new Set(unlocked);
  const affordable = getSentences(snapshot.language).filter((sentence) =>
    marksOf(sentence.text).every((mark) => open.has(mark)),
  );

  const wanted =
    group === null
      ? []
      : affordable.filter((sentence) =>
          group.characters.some((character) => sentence.text.includes(character)),
        );

  const pool = rng.shuffle(wanted.length > 0 ? wanted : affordable);
  const rest = rng.shuffle(affordable.filter((sentence) => !pool.includes(sentence)));

  return [...pool, ...rest].slice(0, count).map((sentence) => sentence.text);
}

/**
 * Сочетания цифр для одного занятия.
 *
 * Учебные и повторительные разведены: кусок, где две цифры новые, а третья
 * старая, не является ни освоением новой пары пальцев, ни повторением старой
 * (ТЗ FR-EN-64). Правило и доля повторения — общие с «Основами»: две копии
 * означали бы, что «повторить» значит разное в соседних режимах.
 */
function digitChunks(
  snapshot: SymbolsSnapshot,
  rng: Rng,
  group: readonly string[],
  known: readonly string[],
  count: number,
): string[] {
  const learning = chunkAlphabet(group, known, MIN_ALPHABET);
  const review = known.length === 0 ? 0 : Math.round(count * symbolReviewShare(snapshot, known));
  const items: string[] = [];

  // Предел разнообразия — тот же, что в «Основах»: набор из двух цифр даёт
  // считанные сочетания, и занятие из них одних превращается в петлю. Пока
  // открытых цифр нет, повторять нечем, и предел не действует.
  const learningCount =
    known.length === 0
      ? count
      : Math.min(count - review, learningChunkLimit(learning, DIGIT_CHUNK_LENGTH));

  for (let index = 0; index < learningCount; index += 1) {
    items.push(buildChunk(rng, learning, DIGIT_CHUNK_LENGTH));
  }
  for (let index = items.length; index < count; index += 1) {
    items.push(buildChunk(rng, known, DIGIT_CHUNK_LENGTH));
  }

  // Повторение перемешано с новым: занятие, у которого вторая половина легче
  // первой, читается как «уже всё».
  return rng.shuffle(items);
}

/**
 * Доля повторения по открытым символам режима.
 *
 * Формула общая для всех режимов, открывающих материал группами; здесь только
 * собираются оценки. Символ открыт лишь после того, как его набирали, так что
 * единиц без статистики тут быть не может.
 */
function symbolReviewShare(snapshot: SymbolsSnapshot, known: readonly string[]): number {
  const scores = known
    .map((character) => snapshot.stats[toMasteryUnitId(`key:${character}`)]?.masteryScore)
    .filter((score): score is number => score !== undefined);

  return reviewShare(scores);
}

/**
 * Сочетание со специальным символом.
 *
 * Символ с Shift ставится между двумя нажатиями базового символа той же
 * клавиши: так тренируется само переключение, а не поиск клавиши. Символ,
 * который набирается без Shift, ставится между двумя другими открытыми
 * символами: три одинаковых нажатия подряд — это удержание клавиши, а не
 * движение.
 */
function specialChunk(
  snapshot: SymbolsSnapshot,
  rng: Rng,
  group: readonly string[],
  known: readonly string[],
): string {
  const character = rng.pick(group);
  const stroke = strokeForCharacter(getLayout(snapshot.layoutId, snapshot.platform), character);

  if (stroke !== null && stroke.requiresShift) {
    return `${stroke.key.base}${character}${stroke.key.base}`;
  }

  const others = [...group, ...known].filter((item) => item !== character);
  if (others.length === 0) return `${character}${character}`;

  const companion = rng.pick(others);
  return `${companion}${character}${companion}`;
}

/** Собирает упражнение по правилам режима. */
function buildExercise(
  snapshot: SymbolsSnapshot,
  mode: SymbolMode,
  seed: string,
  group: SymbolGroup | null,
  unlocked: readonly string[],
): SymbolsExercise {
  const rng = createRng(seed);
  const count = ITEMS_PER_EXERCISE[mode];

  const groupCharacters = group?.characters ?? unlocked;
  const known = unlocked.filter((character) => !groupCharacters.includes(character));

  const items: string[] = [];
  if (mode === 'punctuation') {
    items.push(...sentenceItems(snapshot, rng, group, unlocked, count));
  } else if (mode === 'digits') {
    items.push(...digitChunks(snapshot, rng, groupCharacters, known, count));
  } else {
    // Специальные символы собираются иначе намеренно: символ обрамляется
    // соседом, и это обрамление — часть упражнения, а не примесь старого.
    for (let index = 0; index < count; index += 1) {
      items.push(specialChunk(snapshot, rng, groupCharacters, known));
    }
  }

  return { seed, items, text: items.join(' '), usesSentences: mode === 'punctuation' };
}

/**
 * Строит занятие режима по текущему состоянию навыка.
 *
 * @param snapshot состояние навыка;
 * @param mode режим занятия;
 * @param seed зерно генератора: одно и то же занятие воспроизводимо.
 */
export function buildSymbolsPlan(
  snapshot: SymbolsSnapshot,
  mode: SymbolMode,
  seed: string,
): SymbolsPlan {
  const group = currentGroup(snapshot, mode);
  const unlocked = unlockedCharacters(snapshot, mode);
  const exercise = buildExercise(snapshot, mode, seed, group, unlocked);

  // Единицы занятия — только символы своего режима: буквы предложения
  // тренирует не этот режим, и его итог не должен выдавать себя за их
  // тренировку.
  const own = new Set(unlocked);
  const present = [...new Set([...exercise.text].filter((character) => own.has(character)))];
  const units: MasteryUnit[] = present.map((character) => symbolUnit(snapshot, character));

  return {
    mode,
    language: snapshot.language,
    layoutId: snapshot.layoutId,
    group,
    completedGroupIds: completedGroups(snapshot, mode).map((completed) => completed.id),
    totalGroups: symbolGroups(snapshot, mode).length,
    totalCharacters: symbolGroups(snapshot, mode).reduce(
      (count, item) => count + item.characters.length,
      0,
    ),
    unlockedCharacters: unlocked,
    units,
    exercise,
    shiftHints: shiftHints(snapshot, present),
    hintLevel: HINT_LEVEL[mode],
    finished: group === null,
  };
}
